import { useState } from 'react';
import { createPortal } from 'react-dom';
import { InputField } from '../UI';
import { usePortalDropdown } from '../../hooks/usePortalDropdown';
import type { StaffProfessionResponse } from '../../api/generated';
import styles from './InterventionFormModal.module.css';

// TODO: rebuild this hand-rolled combobox on qampo-ui-library's SearchableDropdown
// instead of raw <button> + createPortal, to stop reimplementing the design system.

interface StaffCompetencePickerProps {
  competences: StaffProfessionResponse[];
  placeholder: string;
  onSelect: (competence: StaffProfessionResponse) => void;
  // Both props together pin a "no competence" entry above the list; omit them to hide it.
  noSkillOptionLabel?: string;
  onSelectNoSkill?: () => void;
}

export const StaffCompetencePicker = ({
  competences,
  placeholder,
  onSelect,
  noSkillOptionLabel,
  onSelectNoSkill
}: StaffCompetencePickerProps) => {
  const [input, setInput] = useState('');
  const { isOpen, position, panelStyle, triggerRef, panelRef, open, close } =
    usePortalDropdown<HTMLDivElement>();

  const filtered = competences.filter((c) =>
    c.name.toLowerCase().includes(input.toLowerCase())
  );
  const hasNoSkillOption = noSkillOptionLabel !== undefined && onSelectNoSkill !== undefined;

  const select = (competence: StaffProfessionResponse) => {
    onSelect(competence);
    setInput('');
    close();
  };

  // Deliberately not reachable via Enter: Enter keeps picking the first filtered competence.
  const selectNoSkill = () => {
    onSelectNoSkill?.();
    setInput('');
    close();
  };

  return (
    <>
      <div ref={triggerRef} className={styles.addRow}>
        <InputField
          type="text"
          value={input}
          placeholder={placeholder}
          onClick={open}
          onChange={(e) => { setInput(e.target.value); open(); }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && filtered.length > 0) select(filtered[0]);
            if (e.key === 'Escape') close();
          }}
        />
      </div>
      {isOpen && (filtered.length > 0 || hasNoSkillOption) && createPortal(
        <div
          ref={panelRef}
          className={styles.skillDropdown}
          style={{ ...panelStyle, width: position.width }}
        >
          {hasNoSkillOption && (
            <button
              type="button"
              className={styles.skillDropdownOption}
              onMouseDown={(e) => {
                e.preventDefault();
                selectNoSkill();
              }}
            >
              {noSkillOptionLabel}
            </button>
          )}
          {filtered.map((competence) => (
            <button
              key={competence.id}
              type="button"
              className={styles.skillDropdownOption}
              onMouseDown={(e) => {
                e.preventDefault();
                select(competence);
              }}
            >
              {competence.name}
            </button>
          ))}
        </div>,
        document.body
      )}
    </>
  );
};
